import React, { useState, useCallback } from 'react';
import { Box, Card, CardHeader, CardContent, CircularProgress, Alert, Button } from '@mui/material';
import { OpenInNew as OpenIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../../contexts/AuthContext';
import { NoteService } from '../../../../services/noteService';
import { ProfileCommentService } from '../../../../services/profileCommentService';
import { MemoryService } from '../../../../services/memoryService';
import { MemoryCategory } from '../../../../types/memory';

import { ActivityFilters } from './ActivityFilters';
import { ActivityComposer } from './ActivityComposer';
import { ActivityItem } from './ActivityItem';
import { ActivityEmpty } from './ActivityEmpty';
import { useUnifiedActivity, type ActivityType } from './useUnifiedActivity';
import type { SelectedMention } from '../../../../types/mention';
import { mentionParser } from '../../../../utils/mention-parser';

interface ActivityFeedProps {
  personId: string;
  personName: string;
  showMetadata?: boolean;
  metadataFormat?: 'short' | 'long';
}

/**
 * ActivityFeed - Unified feed of notes, comments, and memories
 */
export const ActivityFeed: React.FC<ActivityFeedProps> = ({
  personId,
  personName,
  showMetadata = false,
  metadataFormat = 'short'
}) => {
  const { authState } = useAuth();
  const currentUser = authState.user;
  const navigate = useNavigate();
  const [filter, setFilter] = useState<ActivityType>('comment');

  const { items, loading, error, refresh, hasMore, loadMore } = useUnifiedActivity(
    personId,
    filter,
    currentUser?.uid || ''
  );

  /**
   * Handle creating new activity items
   */
  const handleCreate = useCallback(async (type: 'note' | 'comment' | 'memory', content: string, mentions: SelectedMention[]) => {
    if (!currentUser) {
      throw new Error('You must be logged in to post');
    }

    try {
      // Parse mentions from selected mentions
      const parsedMentions = mentionParser.parse(content, mentions);

      switch (type) {
        case 'note':
          await NoteService.createNote(
            {
              personId,
              content,
              isPrivate: false,
              mentions: parsedMentions
            },
            currentUser.uid,
            currentUser.displayName || 'Unknown User',
            currentUser.role
          );
          break;

        case 'comment':
          await ProfileCommentService.createComment(
            {
              content,
              targetType: 'person',
              targetId: personId,
              attachments: [],
              mentions: parsedMentions
            },
            currentUser.uid,
            currentUser.displayName || 'Unknown User',
            currentUser.role,
            personId,
            personName
          );
          break;

        case 'memory':
          await MemoryService.saveMemory(
            {
              personId,
              title: '',
              content,
              category: MemoryCategory.FAMILY_LIFE,
              isDraft: false,
              mentions: parsedMentions
            },
            currentUser.uid,
            currentUser.displayName || 'Unknown User',
            currentUser.role
          );
          break;
      }

      // Refresh feed after creation
      await refresh();
    } catch (err: any) {
      throw new Error(`Failed to create ${type}: ${err.message}`);
    }
  }, [currentUser, personId, personName, refresh]);

  const handleViewAll = useCallback(() => {
    const tabMap: Record<Exclude<ActivityType, 'all'>, string> = {
      comment: 'comments',
      note: 'notes',
      memory: 'memories'
    };

    if (filter !== 'all') {
      navigate(`/people/${personId}/${tabMap[filter]}`);
    }
  }, [filter, personId, navigate]);

  const getViewAllText = () => {
    switch (filter) {
      case 'comment':
        return 'View All Comments';
      case 'note':
        return 'View All Notes';
      case 'memory':
        return 'View All Memories';
      default:
        return 'View All';
    }
  };

  return (
    <Card>
      <CardHeader
        title="Activity & Collaboration"
        action={
          <Button
            size="small"
            variant="outlined"
            onClick={handleViewAll}
            endIcon={<OpenIcon fontSize="small" />}
          >
            {getViewAllText()}
          </Button>
        }
      />

      <CardContent>
        {/* Filters - Controls what type shows and what gets created */}
        <ActivityFilters filter={filter} onChange={setFilter} personId={personId} />

        {/* Inline Composer - Controlled by active filter tab */}
        {currentUser && (
          <ActivityComposer
            personId={personId}
            personName={personName}
            activeFilter={filter}
            onCreate={handleCreate}
          />
        )}

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && items.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {!loading && items.length === 0 && (
          <ActivityEmpty filter={filter} />
        )}

        {/* Activity Items */}
        {items.length > 0 && (
          <Box>
            {items.map((item) => (
              <ActivityItem
                key={`${item.type}-${item.id}`}
                item={item}
                showMetadata={showMetadata}
                metadataFormat={metadataFormat}
              />
            ))}

            {/* Load More */}
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button onClick={loadMore} disabled={loading}>
                  {loading ? 'Loading...' : 'Load More'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
